import React from "react";
import { navigateBack } from "utilities/navigate";
import { ChevronLeftIcon } from "@picklebet/ui";
import cx from "classnames";
import { AnimatePresence, motion } from "motion/react";

import * as styles from "./BackButton.module.scss";

const BackButtonContent = ({
  className,
  onClick,
}: { className: string; onClick?: () => void }) => {
  return (
    <button
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          onClick();
        } else {
          navigateBack();
        }
      }}
      className={className}
    >
      <ChevronLeftIcon />
    </button>
  );
};

type BackButtonProps = {
  className?: string;
  animatePresence?: boolean | null;
  onClick?: () => void; // New optional prop for custom actions
};

/**
 * Display a chevron for users to navigate back, optionally animate it in
 * @param className override for custom styles
 * @param animatePresence when set to null (default), it will not animate in/out
 * @param onClick accept a custom onClick handler (e.g. manual step navigation in signup modal)
 * @constructor
 */
const BackButton = ({
  className = "",
  animatePresence = null,
  onClick,
}: BackButtonProps) => {
  if (animatePresence === null) {
    return (
      <BackButtonContent
        onClick={onClick}
        className={cx({
          [styles.chevronLeft]: true,
          [className]: !!className,
        })}
      />
    );
  }
  const initialStyle = {
    opacity: 0,
    width: 0,
    height: "1.4375rem",
    transform: "translate3d(1.625rem, 0, 0)",
  };

  return (
    <AnimatePresence>
      {animatePresence && (
        <motion.div
          className={styles.motionWrapper}
          initial={initialStyle}
          animate={{
            ...initialStyle,
            opacity: 1,
            width: "1.4375rem",
            transform: "translate3d(0, 0, 0)",
          }}
          exit={initialStyle}
        >
          <BackButtonContent
            onClick={onClick}
            className={cx({
              [styles.chevronLeft]: true,
              [className]: !!className,
            })}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { BackButton };
